import {
  AppError,
  storageError,
  type AppErrorKind,
  type FieldError,
} from '@obraplan/core/application/errors/app-error';
import { ERROR_CODE, messageForCode } from '@obraplan/core/application/errors/error-codes';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { valueObjectReviver } from '@obraplan/core/application/transport/value-object-json';

/**
 * O transporte da aplicação web até a API.
 *
 * Uma função só, e de propósito: todo endpoint é `POST /api/<caso-de-uso>` com JSON, e o
 * `registry` do servidor resolve o caminho pelo nome da classe. Não há verbo a escolher,
 * não há caminho a montar, não há corpo a negociar — o que varia é o nome e o conteúdo.
 *
 * **Nada aqui decide regra.** Este arquivo traduz `Response` em `Result`, e é só isso: a
 * recusa que ele devolve é sempre a que o servidor escreveu, com o mesmo código e a mesma
 * mensagem em português que a tela já sabe exibir. As duas exceções são as falhas que o
 * servidor não teve como responder — rede caída e resposta ilegível —, e as duas ganham
 * código próprio abaixo porque a tela precisa distingui-las de uma recusa de negócio.
 */

export const TRANSPORT_ERROR = {
  /**
   * Não houve resposta.
   *
   * A Fase 12 decidiu que sem internet o sistema não opera; a Fase 43 reverteu isso aos
   * poucos. A leitura chega aqui só quando o aparelho não a tem guardada (`offline-cache.ts`),
   * e a gravação, até a fila da etapa 2, continua recusada. O texto diz as duas coisas: o que
   * já foi aberto aparece, e o que se tentou gravar não foi (RN-601).
   */
  OFFLINE: ERROR_CODE.NO_CONNECTION,
  /**
   * Houve resposta, e não era a nossa.
   *
   * Acontece quando algo entre o navegador e a função responde no lugar dela — portal de
   * Wi-Fi que intercepta, proxy corporativo, a página de erro do provedor de hospedagem.
   * O corpo é HTML, o `JSON.parse` estoura, e sem este ramo a tela mostraria
   * `Unexpected token '<'`, que não ajuda ninguém.
   */
  UNREADABLE: ERROR_CODE.UNREADABLE_RESPONSE,
  /**
   * O corpo passou do tamanho que alguém no caminho aceita (`413`).
   *
   * Quem responde não é a API — é o nginx (`client_max_body_size`) ou o proxy da hospedagem
   * —, e o corpo vem em HTML ou num formato que não é o nosso. Sem este ramo a recusa caía
   * em "resposta ilegível" e mandava a pessoa procurar portal de Wi-Fi por causa de um PDF
   * grande. Tem código próprio porque quem envia arquivo pode fazer algo com ele: a prancha
   * cai para a página renderizada (RN-248).
   */
  TOO_LARGE: ERROR_CODE.REQUEST_TOO_LARGE,
} as const;

function tooLargeError(): AppError {
  return storageError(TRANSPORT_ERROR.TOO_LARGE, messageForCode(ERROR_CODE.REQUEST_TOO_LARGE));
}

export function offlineError(): AppError {
  return storageError(TRANSPORT_ERROR.OFFLINE, messageForCode(ERROR_CODE.NO_CONNECTION));
}

function unreadableError(): AppError {
  return new AppError(
    'unexpected',
    TRANSPORT_ERROR.UNREADABLE,
    messageForCode(ERROR_CODE.UNREADABLE_RESPONSE),
  );
}

/** O corpo de sucesso e o de falha, como `server/api/problem.ts` os escreve. */
interface RespostaOk {
  readonly data: unknown;
}
interface RespostaErro {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly fieldErrors?: readonly FieldError[];
  };
}

export interface ApiClientOptions {
  /**
   * Onde a API responde. Vazio é o normal: em produção a interface e a função moram no
   * mesmo domínio, e caminho relativo evita requisição de outra origem — e o CORS junto.
   */
  readonly baseUrl?: string;
  /**
   * O segredo da sessão, lido a cada chamada e não guardado.
   *
   * Função, e não valor, porque a sessão nasce e morre durante a vida do cliente: guardar
   * o token na construção faria a chamada seguinte a `SignIn` continuar anônima.
   */
  readonly token: () => string | undefined;
  /** Injetável para o teste; em produção é o `fetch` do navegador. */
  readonly fetch?: typeof globalThis.fetch;
}

export interface ApiClient {
  /**
   * `headers` é o que o pedido leva além do token: a fila do aparelho manda o id do passo e a
   * hora em que ele foi feito (Fase 43, RN-606, RN-607).
   */
  post(
    path: string,
    body: unknown,
    options?: {
      readonly headers?: Readonly<Record<string, string>>;
      /**
       * O token deste pedido, no lugar do da sessão: o encerramento da sessão de quem saiu sem
       * sinal vai com o token dela, e não com o de quem entrou depois (RN-616).
       */
      readonly token?: string;
    },
  ): Promise<Result<unknown, AppError>>;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const buscar = options.fetch ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl ?? '';

  return {
    async post(path, body, extras) {
      const token = extras?.token ?? options.token();

      let resposta: Response;
      try {
        resposta = await buscar(`${base}${path}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...extras?.headers,
            ...(token === undefined ? {} : { authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(body ?? {}),
        });
      } catch {
        // `fetch` só rejeita quando não houve resposta: rede caída, DNS, CORS, requisição
        // abortada. Erro de aplicação vem com status, e passa direto por aqui.
        return err(offlineError());
      }

      // Antes de ler o corpo: o `413` vem de quem está no caminho, e o que ele escreve —
      // HTML do nginx, JSON do proxy com outro formato — não diz nada que a tela use.
      if (resposta.status === 413) return err(tooLargeError());

      let corpo: unknown;
      try {
        // `JSON.parse` com reviver, e não `resposta.json()`: é aqui que `Money` e `Hours`
        // voltam a ser objetos com método, e não `{ cents }` cru que quebraria no `.format()`.
        corpo = JSON.parse(await resposta.text(), valueObjectReviver) as unknown;
      } catch {
        return err(unreadableError());
      }

      if (resposta.ok) {
        return ok(temDados(corpo) ? corpo.data : undefined);
      }

      // Status de falha sem o corpo de erro esperado é o mesmo caso do HTML: alguém
      // respondeu no lugar da função.
      return err(temErro(corpo) ? paraAppError(corpo, resposta.status) : unreadableError());
    },
  };
}

function temDados(corpo: unknown): corpo is RespostaOk {
  return typeof corpo === 'object' && corpo !== null && 'data' in corpo;
}

function temErro(corpo: unknown): corpo is RespostaErro {
  if (typeof corpo !== 'object' || corpo === null || !('error' in corpo)) return false;
  const { error: erro } = corpo;
  return typeof erro === 'object' && erro !== null && 'code' in erro && 'message' in erro;
}

/**
 * A recusa do servidor, de volta ao formato que a tela conhece.
 *
 * O `kind` é reconstruído a partir do status, e não vem no corpo — é o que
 * `statusFor` fez na ida, e refazê-lo aqui mantém o `AppError` completo sem acrescentar
 * campo nenhum ao protocolo. A tradução não é perfeita e não precisa ser: quem decide o
 * que fazer é o `code`, que atravessa intacto. O `kind` serve para a tela escolher o tom
 * — vermelho de erro ou aviso de regra —, e para isso o status basta.
 *
 * `details` não volta, e é assim que deve ser: ele carrega a permissão que faltou e o id
 * da obra recusada, cada um uma resposta grátis para quem está sondando
 * (`server/api/problem.ts`).
 */
function paraAppError(corpo: RespostaErro, status: number): AppError {
  const { code, message, fieldErrors } = corpo.error;
  // 401 volta como `forbidden` junto com o 403: o que separa "entre de novo" de "você não
  // pode" é o **código**, que atravessa intacto, e não o `kind`. A tela já decide por ele.
  const kind = KIND_POR_STATUS[status] ?? 'unexpected';
  return new AppError(kind, code, message, { fieldErrors: fieldErrors ?? [] });
}

const KIND_POR_STATUS: Readonly<Record<number, AppErrorKind>> = {
  400: 'validation',
  401: 'forbidden',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
  422: 'business-rule',
  500: 'unexpected',
  507: 'storage',
};
